"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Eye, PhoneCall, ShieldAlert, Wind } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const indicators = [{
  title: "Persistent musty odors",
  desc: "Earthy, damp smells from HVAC vents or closed rooms indicate colonies active inside ductwork or wall cavities.",
  icon: Wind
}, {
  title: "Moisture behind baseboards",
  desc: "Slight drywall discoloration, bubbling paint, or warped trim often signals unseen pipe leaks or slab seepage.",
  icon: Eye
}, {
  title: "Respiratory discomfort",
  desc: "Unexplained morning congestion, eye irritation, or allergy flare-ups often link directly to elevated airborne spore counts.",
  icon: AlertTriangle
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="overview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical spec overline */}
        <div className="flex items-center gap-3 pb-8 border-b border-border mb-12">
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-mono uppercase tracking-wider text-xs px-2.5 py-1">
            IICRC S520 Standards
          </Badge>
          <span className="text-xs text-muted-foreground font-mono">
            Direct Local Response • Maryland, DC &amp; Northern Virginia
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Informational Copy & Hazard Indicators */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp}>
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                Remediation Assessment
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
                Hidden mold spreads quietly before it damages your walls or air.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Catching moisture intrusion early prevents airborne spores from
                circulating through your living spaces and compromising
                structural framing.
              </p>
            </div>

            <p className="text-base text-muted-foreground leading-relaxed">
              Active mold spores need only 24 to 48 hours of uninterrupted
              moisture and stagnant airflow to colonize cellulose insulation,
              subflooring, and sheetrock. When left uncontained, everyday HVAC
              circulation pushes invisible mycotoxins through ventilation
              systems, turning small plumbing seeps into property-wide air
              quality risks. Our field teams isolate the source, establish
              negative air pressure, and remediate the space safely under
              direct insurance documentation.
            </p>

            {/* Diagnostic Indicators */}
            <div className="pt-4 space-y-4">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground">
                Common warning signs that require inspection
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {indicators.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="bg-card border border-border rounded-md p-4 flex flex-col gap-2 hover:border-primary/40 transition-[border-color,box-shadow]">
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <h4 className="text-sm font-semibold text-card-foreground">
                        {item.title}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>;
              })}
              </div>
            </div>

            {/* Direct action strip */}
            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 px-6">
                <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  <span>Call 855-435-6631 for Inspection</span>
                </a>
              </Button>
              <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted h-11 px-5">
                <Link to="/ContactRequestServicePage">
                  Request Containment Setup
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Documentary Photography & Inspection Badge */}
          <motion.div className="lg:col-span-5 relative" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp}>
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_drywall_inspection_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_drywall_inspection_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_drywall_inspection_technician.png-wvc-sizes" alt="Certified restoration technician inspecting mold affected drywall with precision diagnostic task lighting" loading="lazy" decoding="async" className="w-full h-auto aspect-[4/5] object-cover object-center" />

              {/* Technical Inspection Overlay Badge */}
              <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur-xs border border-border rounded-md p-4 shadow-md">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                    <ShieldAlert className="w-4 h-4" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold uppercase tracking-wider text-foreground">
                        Containment Protocol
                      </span>
                      <span className="inline-flex items-center text-[11px] font-medium text-primary gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Verified Negative Air
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      HEPA air scrubbers capture 99.97% of airborne spores down
                      to 0.3 microns during remediation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-strip operational note */}
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground font-mono px-1">
              <span>Field Unit: DC / MD / NoVA</span>
              <span>No Call Centers • Direct Tech Dispatch</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}